const DEFAULT_MAX_BYTES = 2 * 1024 * 1024;
const DEFAULT_MAX_DIMENSION = 512;
const RASTER_IMAGE_TYPES = new Set(['image/png', 'image/jpeg', 'image/jpg', 'image/webp']);
const OUTPUT_QUALITIES = [0.88, 0.8, 0.72, 0.64, 0.56];
const DIMENSION_STEPS = [512, 384, 256, 192, 128];

const getFileType = (file) => String(file?.type || '').toLowerCase();

export const canDownscaleImageFile = (file) => RASTER_IMAGE_TYPES.has(getFileType(file));

const getCanvasBlob = (canvas, type, quality) =>
  new Promise((resolve) => {
    canvas.toBlob((blob) => resolve(blob), type, quality);
  });

const getOutputExtension = (type) => {
  if (type === 'image/jpeg' || type === 'image/jpg') return 'jpg';
  if (type === 'image/png') return 'png';
  return 'webp';
};

const createOutputFileName = (fileName, type) => {
  const base = String(fileName || 'chat-icon').replace(/\.[^.]+$/, '') || 'chat-icon';
  return `${base}.${getOutputExtension(type)}`;
};

const loadImageElement = (file) =>
  new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const image = new Image();
    image.onload = () => {
      URL.revokeObjectURL(url);
      resolve(image);
    };
    image.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error('Kunne ikke lese bildefilen.'));
    };
    image.src = url;
  });

const loadImageSource = async (file) => {
  if (typeof createImageBitmap === 'function') {
    try {
      return createImageBitmap(file, { imageOrientation: 'from-image' });
    } catch {
      // Fall back to the HTMLImageElement path below. Some browsers reject
      // createImageBitmap options for otherwise valid image files.
    }
  }
  return loadImageElement(file);
};

const getImageSize = (image) => ({
  width: image.width || image.naturalWidth || 0,
  height: image.height || image.naturalHeight || 0,
});

const drawScaledImage = (image, sourceWidth, sourceHeight, maxDimension) => {
  const scale = Math.min(1, maxDimension / Math.max(sourceWidth, sourceHeight));
  const width = Math.max(1, Math.round(sourceWidth * scale));
  const height = Math.max(1, Math.round(sourceHeight * scale));
  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  const context = canvas.getContext('2d');
  if (!context) {
    throw new Error('Kunne ikke klargjøre bildet for opplasting.');
  }
  context.drawImage(image, 0, 0, width, height);
  return canvas;
};

const createFileFromBlob = (blob, sourceFile, fallbackType) => {
  const type = blob.type || fallbackType;
  const name = createOutputFileName(sourceFile.name, type);
  if (typeof File !== 'function') {
    return Object.assign(blob, {
      name,
      lastModified: sourceFile.lastModified || Date.now(),
    });
  }
  return new File([blob], name, {
    type,
    lastModified: sourceFile.lastModified || Date.now(),
  });
};

export const downscaleImageFile = async (
  file,
  {
    maxBytes = DEFAULT_MAX_BYTES,
    maxDimension = DEFAULT_MAX_DIMENSION,
    outputType = 'image/webp',
  } = {},
) => {
  if (!file || file.size <= maxBytes || !canDownscaleImageFile(file)) {
    return file;
  }

  const image = await loadImageSource(file);
  try {
    const { width: sourceWidth, height: sourceHeight } = getImageSize(image);
    if (!sourceWidth || !sourceHeight) {
      throw new Error('Kunne ikke lese bildestørrelsen.');
    }

    const dimensionSteps = DIMENSION_STEPS
      .map((dimension) => Math.min(dimension, maxDimension, Math.max(sourceWidth, sourceHeight)))
      .filter((dimension, index, values) => dimension > 0 && values.indexOf(dimension) === index);

    let smallestFile = null;

    for (const dimension of dimensionSteps) {
      const canvas = drawScaledImage(image, sourceWidth, sourceHeight, dimension);
      for (const quality of OUTPUT_QUALITIES) {
        const blob = await getCanvasBlob(canvas, outputType, quality);
        if (!blob) continue;
        const resizedFile = createFileFromBlob(blob, file, outputType);
        if (!smallestFile || resizedFile.size < smallestFile.size) {
          smallestFile = resizedFile;
        }
        if (resizedFile.size <= maxBytes) {
          return resizedFile;
        }
      }
    }

    return smallestFile || file;
  } finally {
    if (typeof image.close === 'function') {
      image.close();
    }
  }
};
